import { useEffect, useRef } from 'react';
import { gsap } from 'gsap';

const GOLD = '251, 191, 36';

function playPortalSound() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const now = ctx.currentTime;
    [400, 600, 800].forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = i === 2 ? 'triangle' : 'sine';
      osc.frequency.setValueAtTime(freq, now);
      osc.frequency.exponentialRampToValueAtTime(freq * 1.5, now + 0.4);
      gain.gain.setValueAtTime(0.04, now + i * 0.05);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.45 + i * 0.05);
      osc.start(now + i * 0.05);
      osc.stop(now + 0.5 + i * 0.05);
    });
  } catch (e) { /* Audio not available */ }
}

function spawnEdgeParticles(cx, cy, radius, count, container) {
  const particles = [];
  for (let i = 0; i < count; i++) {
    const angle = (Math.PI * 2 * i) / count + (Math.random() - 0.5) * 0.4;
    const x = cx + Math.cos(angle) * radius;
    const y = cy + Math.sin(angle) * radius;
    const size = 2 + Math.random() * 4;

    const el = document.createElement('div');
    el.style.cssText = `
      position: fixed;
      width: ${size}px;
      height: ${size}px;
      border-radius: 50%;
      background: rgba(${GOLD}, ${0.5 + Math.random() * 0.5});
      box-shadow: 0 0 ${size * 3}px rgba(${GOLD}, 0.5);
      pointer-events: none;
      z-index: 10002;
      left: ${x}px;
      top: ${y}px;
    `;
    container.appendChild(el);
    particles.push(el);

    // Drift outward from circle edge
    const outX = Math.cos(angle) * (30 + Math.random() * 60);
    const outY = Math.sin(angle) * (30 + Math.random() * 60);

    gsap.to(el, {
      x: outX,
      y: outY - 20,
      opacity: 0,
      scale: 0,
      duration: 0.5 + Math.random() * 0.4,
      ease: 'power2.out',
      delay: Math.random() * 0.15,
      onComplete: () => el.remove(),
    });
  }
  return particles;
}

export default function PortalTransition({ isActive, isReversing, onComplete, homeContentRef, silent = false }) {
  const overlayRef = useRef(null);
  const ringRef = useRef(null);
  const flashRef = useRef(null);
  const clipRef = useRef(null);

  useEffect(() => {
    if (!isActive || !overlayRef.current) return;

    const overlay = overlayRef.current;
    const ring = ringRef.current;
    const flash = flashRef.current;
    const clipEl = clipRef.current;
    const homeContent = homeContentRef?.current;
    const allParticles = [];

    const cx = window.innerWidth / 2;
    const cy = window.innerHeight / 2;
    const maxRadius = Math.hypot(cx, cy);

    if (isReversing) {
      const tl = gsap.timeline({ onComplete: () => onComplete?.() });

      gsap.set(overlay, { opacity: 1 });
      gsap.set(clipEl, { clipPath: `circle(${maxRadius}px at ${cx}px ${cy}px)`, opacity: 1 });
      gsap.set(flash, { opacity: 0, scale: 0 });
      gsap.set(ring, { scale: 0, opacity: 0 });

      // Quick flash
      tl.to(flash, { scale: 1.5, opacity: 0.5, duration: 0.2, ease: 'power2.out' }, 0);
      tl.to(flash, { opacity: 0, duration: 0.2 }, 0.15);

      // Circle shrinks back to center
      tl.to(clipEl, {
        clipPath: `circle(0px at ${cx}px ${cy}px)`,
        duration: 0.7,
        ease: 'power2.in',
      }, 0.1);

      // Overlay fades
      tl.to(overlay, { opacity: 0, duration: 0.3, ease: 'power2.out' }, 0.6);

      // Restore home content
      if (homeContent) {
        tl.fromTo(homeContent,
          { opacity: 0, scale: 0.92, filter: 'blur(3px)' },
          { opacity: 1, scale: 1, filter: 'blur(0px)', duration: 0.5, ease: 'power2.out' },
          0.4
        );
      }

      return () => tl.kill();
    }

    // --- Forward: "Golden Eclipse" ---
    if (!silent) playPortalSound();

    const tl = gsap.timeline({ onComplete: () => onComplete?.() });

    // Init
    gsap.set(overlay, { opacity: 1 });
    gsap.set(clipEl, { clipPath: `circle(0px at ${cx}px ${cy}px)`, opacity: 1 });
    gsap.set(ring, { scale: 0, opacity: 0 });
    gsap.set(flash, { scale: 0, opacity: 0 });

    // Phase 1: Pulse — heartbeat
    if (homeContent) {
      tl.to(homeContent, { scale: 1.03, duration: 0.08, ease: 'power2.out' }, 0);
      tl.to(homeContent, { scale: 1, duration: 0.08, ease: 'power2.in' }, 0.08);
    }

    // Phase 2: Shockwave ring
    tl.to(ring, {
      scale: 4,
      opacity: 0.8,
      duration: 0.5,
      ease: 'power2.out',
    }, 0.1);
    tl.to(ring, {
      opacity: 0,
      duration: 0.3,
      ease: 'power2.in',
    }, 0.35);

    // Phase 3: Dark circle expands (clip-path)
    const expandDuration = 0.8;
    tl.to(clipEl, {
      clipPath: `circle(${maxRadius}px at ${cx}px ${cy}px)`,
      duration: expandDuration,
      ease: 'power2.inOut',
    }, 0.2);

    // Phase 4: Edge particles — spawn in waves as circle grows
    const particleWaves = 3;
    for (let w = 0; w < particleWaves; w++) {
      const delay = 0.3 + w * 0.2;
      const waveRadius = (maxRadius * (w + 1)) / particleWaves * 0.6;
      tl.call(() => {
        const p = spawnEdgeParticles(cx, cy, waveRadius, 12, document.body);
        allParticles.push(...p);
      }, [], delay);
    }

    // Phase 5: Warp home content
    if (homeContent) {
      tl.to(homeContent, {
        scale: 0.85,
        filter: 'blur(6px)',
        opacity: 0,
        duration: 0.6,
        ease: 'power3.in',
      }, 0.2);
    }

    // Phase 6: Golden flash pulse inside the dark overlay
    tl.to(flash, {
      scale: 3,
      opacity: 0.7,
      duration: 0.3,
      ease: 'power2.out',
    }, 0.85);
    tl.to(flash, {
      opacity: 0,
      duration: 0.3,
      ease: 'power2.in',
    }, 1.05);

    // Phase 7: Overlay fades to reveal birthday page
    tl.to(overlay, {
      opacity: 0,
      duration: 0.4,
      ease: 'power2.out',
    }, 1.1);

    return () => {
      tl.kill();
      allParticles.forEach(p => p.parentNode?.removeChild(p));
    };
  }, [isActive, isReversing, onComplete, homeContentRef, silent]);

  if (!isActive) return null;

  const cx = window.innerWidth / 2;
  const cy = window.innerHeight / 2;

  return (
    <div
      ref={overlayRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 10000,
        pointerEvents: 'none',
        opacity: 0,
      }}
    >
      {/* Expanding dark circle (clip-path reveal) */}
      <div
        ref={clipRef}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: 'radial-gradient(ellipse at center, #111 0%, #0a0a0a 60%, #050505 100%)',
          clipPath: `circle(0px at ${cx}px ${cy}px)`,
        }}
      />

      {/* Shockwave ring */}
      <div
        ref={ringRef}
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          width: '120px',
          height: '120px',
          marginLeft: '-60px',
          marginTop: '-60px',
          borderRadius: '50%',
          border: `2px solid rgba(${GOLD}, 0.7)`,
          boxShadow: `0 0 30px rgba(${GOLD}, 0.3), inset 0 0 20px rgba(${GOLD}, 0.15)`,
          transform: 'scale(0)',
          opacity: 0,
        }}
      />

      {/* Golden flash */}
      <div
        ref={flashRef}
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          width: '200px',
          height: '200px',
          marginLeft: '-100px',
          marginTop: '-100px',
          borderRadius: '50%',
          background: `radial-gradient(circle, rgba(${GOLD}, 0.6) 0%, rgba(245, 158, 11, 0.3) 40%, transparent 70%)`,
          filter: 'blur(30px)',
          transform: 'scale(0)',
          opacity: 0,
        }}
      />
    </div>
  );
}
